// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
"use strict";
var _a, _b, _c;
import dynamicProto from "@microsoft/dynamicproto-js";
import { AnalyticsPlugin } from "@microsoft/applicationinsights-analytics-js";
import { CfgSyncPlugin } from "@microsoft/applicationinsights-cfgsync-js";
import { Sender } from "@microsoft/applicationinsights-channel-js";
import { AnalyticsPluginIdentifier, AppInsightsCore, DEFAULT_BREEZE_PATH, PropertiesPluginIdentifier, ThrottleMgr, _throwInternal, addPageHideEventListener, addPageUnloadEventListener, cfgDfMerge, cfgDfValidate, createDynamicConfig, createOTelApi, createProcessTelemetryContext, createSdkStatsNotifCbk, createTraceProvider, createUniqueNamespace, doPerf, hasDocument, hasWindow, isArray, isFeatureEnabled, isFunction, isNullOrUndefined, isReactNative, isString, mergeEvtNamespace, onConfigChange, parseConnectionString, proxyAssign, proxyFunctions, removePageHideEventListener, removePageUnloadEventListener, useSpan } from "@microsoft/applicationinsights-core-js";
import { AjaxPlugin as DependenciesPlugin } from "@microsoft/applicationinsights-dependencies-js";
import { PropertiesPlugin } from "@microsoft/applicationinsights-properties-js";
import { createPromise, createSyncPromise, doAwaitResponse } from "@nevware21/ts-async";
import { arrForEach, arrIndexOf, dumpObj, getDeferred, isPromiseLike, objDefine, objForEachKey, strIndexOf, throwUnsupported } from "@nevware21/ts-utils";
import { CONFIG_ENDPOINT_URL, STR_ADD_TELEMETRY_INITIALIZER, STR_CLEAR_AUTHENTICATED_USER_CONTEXT, STR_EVT_NAMESPACE, STR_FLUSH, STR_GET_COOKIE_MGR, STR_GET_PLUGIN, STR_POLL_INTERNAL_LOGS, STR_SET_AUTHENTICATED_USER_CONTEXT, STR_SNIPPET, STR_START_TRACK_EVENT, STR_START_TRACK_PAGE, STR_STOP_TRACK_EVENT, STR_STOP_TRACK_PAGE, STR_TRACK_DEPENDENCY_DATA, STR_TRACK_EVENT, STR_TRACK_EXCEPTION, STR_TRACK_METRIC, STR_TRACK_PAGE_VIEW, STR_TRACK_TRACE, UNDEFINED_VALUE } from "./InternalConstants";
import { _DYN_ADD_HOUSEKEEPING_BEF1, _DYN_CONNECTION_STRING, _DYN_CONTEXT, _DYN_ENDPOINT_URL, _DYN_INSTRUMENTATION_KEY, _DYN_LOGGER, _DYN_ONUNLOAD_FLUSH, _DYN_QUEUE, _DYN_REMOVE_NOTIFICATION_2, _DYN_SEND_MESSAGE, _DYN_UNLOAD, _DYN_UPDATE_SNIPPET_DEFIN3, _DYN_USER_OVERRIDE_ENDPOI0, _DYN_VERSION } from "./__DynamicConstants";
import { createTelemetryItemFromSpan } from "./internal/trace/spanUtils";
var _internalSdkSrc;
var STR_DEPENDENCIES = "dependencies";
var STR_PROPERTIES = "properties";
var STR_SNIPPET_VERSION = "_snippetVersion";
var STR_APP_INSIGHTS_NEW = "appInsightsNew";
var STR_GET_SKU_DEFAULTS = "getSKUDefaults";
// This is an exclude list of properties that should not be updated during initialization
// They include a combination of private and internal property names
var _ignoreUpdateSnippetProperties = [
    STR_SNIPPET, STR_DEPENDENCIES, STR_PROPERTIES, STR_SNIPPET_VERSION, STR_APP_INSIGHTS_NEW, STR_GET_SKU_DEFAULTS, "trace", "otelApi"
];
// This is an exclude list of properties that should not be proxied to the snippet
// They include a combination of private and internal property names
var _ignoreProxyAssignProperties = [
    STR_SNIPPET, STR_DEPENDENCIES, STR_PROPERTIES, STR_SNIPPET_VERSION, STR_APP_INSIGHTS_NEW, STR_GET_SKU_DEFAULTS
];
var IKEY_USAGE = "iKeyUsage";
var CDN_USAGE = "CdnUsage";
var SDK_LOADER_VER = "SdkLoaderVer";
var ZIP_PAYLOAD = "zipPayload";
var SDK_STATS = "SdkStats";
var _sdkVersion = '3.4.3';
var default_limit = {
    samplingRate: 100,
    maxSendNumber: 1
};
var default_interval = {
    monthInterval: 3,
    daysOfMonth: [28]
};
var default_throttle_config = {
    disabled: true,
    limit: cfgDfMerge(default_limit),
    interval: cfgDfMerge(default_interval)
};
// We need to include all properties that we only reference that we want to be dynamically updatable here
// So they are converted even when not specified in the passed configuration
var defaultConfigValues = {
    connectionString: UNDEFINED_VALUE,
    endpointUrl: UNDEFINED_VALUE,
    instrumentationKey: UNDEFINED_VALUE,
    userOverrideEndpointUrl: UNDEFINED_VALUE,
    diagnosticLogInterval: cfgDfValidate(_chkDiagLevel, 10000),
    featureOptIn: (_a = {},
        _a[IKEY_USAGE] = { mode: 3 /* FeatureOptInMode.enable */ },
        _a[CDN_USAGE] = { mode: 2 /* FeatureOptInMode.disable */ },
        _a[SDK_LOADER_VER] = { mode: 2 /* FeatureOptInMode.disable */ },
        _a[ZIP_PAYLOAD] = { mode: 1 /* FeatureOptInMode.none */ },
        _a[SDK_STATS] = { mode: 3 /* FeatureOptInMode.enable */ },
        _a),
    sdkStats: cfgDfMerge({
        int: 900000
    }),
    throttleMgrCfg: cfgDfMerge((_b = {},
        _b[109 /* _eInternalMessageId.DefaultThrottleMsgKey */] = cfgDfMerge(default_throttle_config),
        _b[106 /* _eInternalMessageId.InstrumentationKeyDeprecation */] = cfgDfMerge(default_throttle_config),
        _b[111 /* _eInternalMessageId.SdkLdrUpdate */] = cfgDfMerge(default_throttle_config),
        _b[110 /* _eInternalMessageId.CdnDeprecation */] = cfgDfMerge(default_throttle_config),
        _b)),
    extensionConfig: cfgDfMerge((_c = {},
        _c["AppInsightsCfgSyncPlugin"] = cfgDfMerge({
            cfgUrl: CONFIG_ENDPOINT_URL,
            syncMode: 2 /* ICfgSyncMode.Receive */
        }),
        _c))
};
function _chkDiagLevel(value) {
    // Make sure we have a value > 0
    return value && value > 0;
}
function _parseCs(config, configCs) {
    return createSyncPromise(function (resolve, reject) {
        doAwaitResponse(configCs, function (res) {
            var curCs = res && res.value;
            var parsedCs = null;
            if (!res.rejected && curCs) {
                // replace cs with resolved values in case of circular promises
                config[_DYN_CONNECTION_STRING /* @min:%2econnectionString */] = curCs;
                parsedCs = parseConnectionString(curCs);
            }
            // if can't resolve cs promise, null will be returned
            resolve(parsedCs);
        });
    });
}
function _initOTel(sku, traceName, onEnd, onException) {
    var otelApi = getDeferred(createOTelApi, [{
            host: sku
        }]);
    // Create the initial default traceProvider
    sku.core.setTraceProvider(getDeferred(function () {
        return createTraceProvider(sku, traceName, otelApi.v, onEnd, onException);
    }));
    return otelApi;
}
/**
 * Application Insights API
 * @group Entrypoint
 * @group Classes
 */
var AppInsightsSku = /** @class */ (function () {
    function AppInsightsSku(snippet) {
        var _this = this;
        // NOTE!: DON'T set default values here, instead set them in the _initDefaults() function as it is also called during teardown()
        var dependencies;
        var properties;
        var _sender;
        var _snippetVersion;
        var _evtNamespace;
        var _houseKeepingNamespace;
        var _core;
        var _config;
        var _analyticsPlugin;
        var _cfgSyncPlugin;
        var _throttleMgr;
        var _iKeySentMessage;
        var _cdnSentMessage;
        var _sdkVerSentMessage;
        var _otelApi;
        var _sdkStatsListener;
        dynamicProto(AppInsightsSku, this, function (_self) {
            _initDefaults();
            objDefine(_self, "config", {
                g: function () {
                    return _config;
                }
            });
            arrForEach(["pluginVersionStringArr", "pluginVersionString"], function (key) {
                objDefine(_self, key, {
                    g: function () {
                        if (_core) {
                            return _core[key];
                        }
                        return null;
                    }
                });
            });
            // initialize the queue and config in case they are undefined
            _snippetVersion = "" + (snippet.sv || snippet[_DYN_VERSION /* @min:%2eversion */] || "");
            snippet[_DYN_QUEUE /* @min:%2equeue */] = snippet[_DYN_QUEUE /* @min:%2equeue */] || [];
            snippet[_DYN_VERSION /* @min:%2eversion */] = snippet[_DYN_VERSION /* @min:%2eversion */] || 2.0; // Default to new version
            var cfgHandler = createDynamicConfig(snippet.config || {}, defaultConfigValues);
            _config = cfgHandler.cfg;
            _analyticsPlugin = new AnalyticsPlugin();
            objDefine(_self, "appInsights", {
                g: function () {
                    return _analyticsPlugin;
                }
            });
            properties = new PropertiesPlugin();
            dependencies = new DependenciesPlugin();
            _sender = new Sender();
            _core = new AppInsightsCore();
            objDefine(_self, "core", {
                g: function () {
                    return _core;
                }
            });
            objDefine(_self, "otelApi", {
                g: function () {
                    return _otelApi ? _otelApi.v : null;
                }
            });
            objDefine(_self, "trace", {
                g: function () {
                    return _otelApi ? _otelApi.v.trace : null;
                }
            });
            // Will get recalled if any referenced values are changed
            _addUnloadHook(onConfigChange(cfgHandler, function () {
                var configCs = _config[_DYN_CONNECTION_STRING /* @min:%2econnectionString */];
                if (isPromiseLike(configCs)) {
                    var ikeyPromise = createSyncPromise(function (resolve, reject) {
                        doAwaitResponse(_parseCs(_config, configCs), function (rsp) {
                            if (!rsp.rejected) {
                                var ikey = _config[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */];
                                var cs = rsp.value;
                                ikey = cs && cs.instrumentationkey || ikey;
                                resolve(ikey);
                            }
                            else {
                                // parseCs will always resolve(unless timeout)
                                // return null in case any error happens
                                resolve(null);
                            }
                        });
                    });
                    var url = _config[_DYN_USER_OVERRIDE_ENDPOI0 /* @min:%2euserOverrideEndpointUrl */];
                    if (isNullOrUndefined(url)) {
                        url = createSyncPromise(function (resolve, reject) {
                            doAwaitResponse(_parseCs(_config, configCs), function (rsp) {
                                if (!rsp.rejected) {
                                    var url_1 = _config[_DYN_ENDPOINT_URL /* @min:%2eendpointUrl */];
                                    var cs = rsp.value;
                                    var ingest = cs && cs.ingestionendpoint;
                                    url_1 = ingest ? ingest + DEFAULT_BREEZE_PATH : url_1;
                                    resolve(url_1);
                                }
                                else {
                                    // parseCs will always resolve(unless timeout)
                                    // return null in case any error happens
                                    resolve(null);
                                }
                            });
                        });
                    }
                    _config[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */] = ikeyPromise;
                    _config[_DYN_ENDPOINT_URL /* @min:%2eendpointUrl */] = url;
                }
                if (isString(configCs) && configCs) {
                    // confirm if promiselike function present
                    // handle cs promise here
                    // add cases to oneNote
                    var cs = parseConnectionString(configCs);
                    var ingest = cs.ingestionendpoint;
                    _config.endpointUrl = _config[_DYN_USER_OVERRIDE_ENDPOI0 /* @min:%2euserOverrideEndpointUrl */] ? _config[_DYN_USER_OVERRIDE_ENDPOI0 /* @min:%2euserOverrideEndpointUrl */] : ingest + DEFAULT_BREEZE_PATH; // add /v2/track
                    _config[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */] = cs.instrumentationkey || _config[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */];
                }
                // userOverrideEndpointUrl have the highest priority
                _config.endpointUrl = _config[_DYN_USER_OVERRIDE_ENDPOI0 /* @min:%2euserOverrideEndpointUrl */] ? _config[_DYN_USER_OVERRIDE_ENDPOI0 /* @min:%2euserOverrideEndpointUrl */] : _config[_DYN_ENDPOINT_URL /* @min:%2eendpointUrl */];
            }));
            _self[STR_SNIPPET /* @min:%2esnippet */] = snippet;
            _self[STR_FLUSH /* @min:%2eflush */] = function (isAsync, callBack) {
                if (isAsync === void 0) { isAsync = true; }
                var result;
                doPerf(_core, function () { return "AISKU.flush"; }, function () {
                    if (isAsync && !callBack) {
                        result = createPromise(function (resolve) {
                            callBack = resolve;
                        });
                    }
                    var waiting = 1;
                    var flushDone = function () {
                        waiting--;
                        if (waiting === 0) {
                            callBack();
                        }
                    };
                    arrForEach(_core.getChannels(), function (channel) {
                        if (channel) {
                            waiting++;
                            channel[STR_FLUSH /* @min:%2eflush */](isAsync, flushDone);
                        }
                    });
                    // decrement the initial "waiting"
                    flushDone();
                }, null, isAsync);
                return result;
            };
            _self[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */] = function (isAsync) {
                if (isAsync === void 0) { isAsync = true; }
                arrForEach(_core.getChannels(), function (channel) {
                    if (channel[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */]) {
                        channel[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */]();
                    }
                    else {
                        channel[STR_FLUSH /* @min:%2eflush */](isAsync);
                    }
                });
            };
            _self.loadAppInsights = function (legacyMode, logger, notificationManager) {
                if (legacyMode === void 0) { legacyMode = false; }
                if (legacyMode) {
                    throwUnsupported("Legacy Mode is no longer supported");
                }
                function _updateSnippetProperties(snippet) {
                    if (snippet) {
                        var snippetVer = "";
                        if (!isNullOrUndefined(_snippetVersion)) {
                            snippetVer += _snippetVersion;
                        }
                        if (_self[_DYN_CONTEXT /* @min:%2econtext */] && _self[_DYN_CONTEXT /* @min:%2econtext */].internal) {
                            _self[_DYN_CONTEXT /* @min:%2econtext */].internal.snippetVer = snippetVer || "-";
                        }
                        // apply updated properties to the global instance (snippet)
                        objForEachKey(_self, function (field, value) {
                            if (isString(field) &&
                                !isFunction(value) &&
                                field && field[0] !== "_" && // Don't copy "internal" values
                                arrIndexOf(_ignoreUpdateSnippetProperties, field) === -1) {
                                if (snippet[field] !== value) {
                                    snippet[field] = value;
                                }
                            }
                        });
                    }
                }
                doPerf(_self.core, function () { return "AISKU.loadAppInsights"; }, function () {
                    // initialize core
                    _core.initialize(_config, [_sender, properties, dependencies, _analyticsPlugin, _cfgSyncPlugin], logger, notificationManager);
                    // Initialize the initial OTel API
                    _otelApi = _initOTel(_self, "aisku", _onEnd, _onException);
                    objDefine(_self, "context", {
                        g: function () { return properties[_DYN_CONTEXT /* @min:%2econtext */]; }
                    });
                    if (!_throttleMgr) {
                        _throttleMgr = new ThrottleMgr(_core);
                    }
                    var sdkSrc = _findSdkSourceFile();
                    if (sdkSrc && _self[_DYN_CONTEXT /* @min:%2econtext */]) {
                        _self[_DYN_CONTEXT /* @min:%2econtext */].internal.sdkSrc = sdkSrc;
                    }
                    _updateSnippetProperties(_self[STR_SNIPPET /* @min:%2esnippet */]);
                    // Empty queue of all api calls logged prior to sdk download
                    _self.emptyQueue();
                    _self[STR_POLL_INTERNAL_LOGS /* @min:%2epollInternalLogs */]();
                    _self[_DYN_ADD_HOUSEKEEPING_BEF1 /* @min:%2eaddHousekeepingBeforeUnload */](_self);
                    _addUnloadHook(onConfigChange(cfgHandler, function () {
                        var defaultEnable = false;
                        if (_config.throttleMgrCfg[109 /* _eInternalMessageId.DefaultThrottleMsgKey */]) {
                            defaultEnable = !_config.throttleMgrCfg[109 /* _eInternalMessageId.DefaultThrottleMsgKey */].disabled;
                        }
                        if (!_throttleMgr.isReady() && _config.extensionConfig && _config.extensionConfig[_cfgSyncPlugin.identifier] && defaultEnable) {
                            // set ready state to true will automatically trigger flush()
                            _throttleMgr.onReadyState(true);
                        }
                        if (!_iKeySentMessage && !_config[_DYN_CONNECTION_STRING /* @min:%2econnectionString */] && isFeatureEnabled(IKEY_USAGE, _config, true)) {
                            _throttleMgr[_DYN_SEND_MESSAGE /* @min:%2esendMessage */](106 /* _eInternalMessageId.InstrumentationKeyDeprecation */, "See Instrumentation key support at aka.ms/IkeyMigrate");
                            _iKeySentMessage = true;
                        }
                        if (!_cdnSentMessage && _self[_DYN_CONTEXT /* @min:%2econtext */].internal.sdkSrc && _self[_DYN_CONTEXT /* @min:%2econtext */].internal.sdkSrc.indexOf("az416426") != -1 && isFeatureEnabled(CDN_USAGE, _config, true)) {
                            _throttleMgr[_DYN_SEND_MESSAGE /* @min:%2esendMessage */](110 /* _eInternalMessageId.CdnDeprecation */, "See Cdn support notice at aka.ms/JsActiveCdn");
                            _cdnSentMessage = true;
                        }
                        if (!_sdkVerSentMessage && parseInt(_snippetVersion) < 6 && isFeatureEnabled(SDK_LOADER_VER, _config, true)) {
                            _throttleMgr[_DYN_SEND_MESSAGE /* @min:%2esendMessage */](111 /* _eInternalMessageId.SdkLdrUpdate */, "An updated Sdk Loader is available, see aka.ms/SnippetVer");
                            _sdkVerSentMessage = true;
                        }
                        // Enable/disable SDK Stats listener dynamically based on config
                        if (isFeatureEnabled(SDK_STATS, _config, true)) {
                            if (!_sdkStatsListener) {
                                _sdkStatsListener = createSdkStatsNotifCbk(_core, _sdkVersion);
                                _core.addNotificationListener(_sdkStatsListener);
                            }
                        }
                        else if (_sdkStatsListener) {
                            try {
                                _sdkStatsListener[_DYN_UNLOAD /* @min:%2eunload */]();
                                _core[_DYN_REMOVE_NOTIFICATION_2 /* @min:%2eremoveNotificationListener */](_sdkStatsListener);
                            }
                            catch (e) {
                                // Swallow any errors
                            }
                            _sdkStatsListener = null;
                        }
                    }));
                });
                return _self;
            };
            _self[_DYN_UPDATE_SNIPPET_DEFIN3 /* @min:%2eupdateSnippetDefinitions */] = function (snippet) {
                // apply full appInsights to the global instance
                // Note: This must be called before loadAppInsights is called
                proxyAssign(snippet, _self, function (name) {
                    // Not excluding names prefixed with "_" as we need to proxy some functions like _onError
                    return name && arrIndexOf(_ignoreProxyAssignProperties, name) === -1;
                });
            };
            _self.emptyQueue = function () {
                // call functions that were queued before the main script was loaded
                try {
                    if (isArray(_self.snippet[_DYN_QUEUE /* @min:%2equeue */])) {
                        // note: do not check length in the for-loop conditional in case something goes wrong and the stub methods are not overridden.
                        var length_1 = _self.snippet[_DYN_QUEUE /* @min:%2equeue */].length;
                        for (var i = 0; i < length_1; i++) {
                            var call = _self.snippet[_DYN_QUEUE /* @min:%2equeue */][i];
                            call();
                        }
                        _self.snippet[_DYN_QUEUE /* @min:%2equeue */] = undefined;
                        delete _self.snippet[_DYN_QUEUE /* @min:%2equeue */];
                    }
                }
                catch (exception) {
                    var properties_1 = {};
                    if (exception && isFunction(exception.toString)) {
                        properties_1.exception = exception.toString();
                    }
                    // need from core
                    // Microsoft.ApplicationInsights._InternalLogging.throwInternal(
                    //     eLoggingSeverity.WARNING,
                    //     _eInternalMessageId.FailedToSendQueuedTelemetry,
                    //     "Failed to send queued telemetry",
                    //     properties);
                }
            };
            _self[_DYN_ADD_HOUSEKEEPING_BEF1 /* @min:%2eaddHousekeepingBeforeUnload */] = function (appInsightsInstance) {
                // Add callback to push events when the user navigates away
                if (hasWindow() || hasDocument()) {
                    var performHousekeeping_1 = function () {
                        // Adds the ability to flush all data before the page unloads.
                        // Note: This approach tries to push a sync request with all the pending events onbeforeunload.
                        // Firefox does not respect this.Other browsers DO push out the call with < 100% hit rate.
                        // Telemetry here will help us analyze how effective this approach is.
                        // Another approach would be to make this call sync with a acceptable timeout to reduce the
                        // impact on user experience.
                        // appInsightsInstance.context._sender.triggerSend();
                        appInsightsInstance[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */](false);
                        // Back up the current session to local storage
                        // This lets us close expired sessions after the cookies themselves expire
                        if (isFunction(_self.core[STR_GET_PLUGIN /* @min:%2egetPlugin */])) {
                            var loadedPlugin = _this.core[STR_GET_PLUGIN /* @min:%2egetPlugin */](PropertiesPluginIdentifier);
                            if (loadedPlugin) {
                                var propertiesPlugin = loadedPlugin.plugin;
                                if (propertiesPlugin && propertiesPlugin[_DYN_CONTEXT /* @min:%2econtext */] && propertiesPlugin[_DYN_CONTEXT /* @min:%2econtext */]._sessionManager) {
                                    propertiesPlugin[_DYN_CONTEXT /* @min:%2econtext */]._sessionManager.backup();
                                }
                            }
                        }
                    };
                    var added_1 = false;
                    if (!_houseKeepingNamespace) {
                        _houseKeepingNamespace = mergeEvtNamespace(_evtNamespace, _core[STR_EVT_NAMESPACE /* @min:%2eevtNamespace */] && _core[STR_EVT_NAMESPACE /* @min:%2eevtNamespace */]());
                    }
                    // Will be recalled if any referenced config properties change
                    _addUnloadHook(onConfigChange(_config, function (details) {
                        var coreConfig = details.cfg;
                        var analyticsPlugin = appInsightsInstance.appInsights;
                        var ctx = createProcessTelemetryContext(null, coreConfig, analyticsPlugin.core);
                        var extConfig = ctx.getExtCfg(analyticsPlugin.identifier || AnalyticsPluginIdentifier);
                        // As we could get recalled, remove any previously registered event handlers first
                        _removePageEventHandlers();
                        var excludePageUnloadEvents = coreConfig.disablePageUnloadEvents;
                        if (!extConfig.disableFlushOnBeforeUnload) {
                            // Hook the unload event for the document, window and body to ensure that the client events are flushed to the server
                            // As just hooking the window does not always fire (on chrome) for page navigation's.
                            if (addPageUnloadEventListener(performHousekeeping_1, excludePageUnloadEvents, _houseKeepingNamespace)) {
                                added_1 = true;
                            }
                            // We also need to hook the pagehide and visibilitychange events as not all versions of Safari support load/unload events.
                            if (addPageHideEventListener(performHousekeeping_1, excludePageUnloadEvents, _houseKeepingNamespace)) {
                                added_1 = true;
                            }
                            // A reactNative app may not have a window and therefore the beforeunload/pagehide events -- so don't
                            // log the failure in this case
                            if (!added_1 && !isReactNative()) {
                                _throwInternal(_core[_DYN_LOGGER /* @min:%2elogger */], 1 /* eLoggingSeverity.CRITICAL */, 19 /* _eInternalMessageId.FailedToAddHandlerForOnBeforeUnload */, "Could not add handler for beforeunload and pagehide");
                            }
                        }
                        if (!added_1 && !extConfig.disableFlushOnUnload) {
                            // If we didn't add the normal set then attempt to add the pagehide and visibilitychange only
                            addPageHideEventListener(performHousekeeping_1, excludePageUnloadEvents, _houseKeepingNamespace);
                        }
                    }));
                }
            };
            _self.getSender = function () {
                return _sender;
            };
            _self[_DYN_UNLOAD /* @min:%2eunload */] = function (isAsync, unloadComplete, cbTimeout) {
                var unloadDone = false;
                var result;
                if (isAsync !== false && !unloadComplete) {
                    result = createPromise(function (resolve) {
                        // Set the callback to the promise resolve callback
                        unloadComplete = resolve;
                    });
                }
                function _unloadCallback(unloadState) {
                    if (!unloadDone) {
                        unloadDone = true;
                        // Reset OTel API to clean up all trace state before unloading core
                        if (_core) {
                            // Clear the trace provider to stop any active spans
                            _core.setTraceProvider(null);
                            // Reset the OTel API instances - this will be recreated on next init
                            if (_otelApi) {
                                _otelApi.v.shutdown();
                            }
                            _otelApi = null;
                        }
                        _initDefaults();
                        unloadComplete && unloadComplete(unloadState);
                    }
                }
                _removePageEventHandlers();
                // Unload SDK Stats listener BEFORE flushing the channel.
                // unload() calls core.track() to enqueue the accumulated metrics,
                // then the single onunloadFlush below sends everything (regular telemetry + SDK stats).
                if (_sdkStatsListener && _core) {
                    try {
                        _sdkStatsListener[_DYN_UNLOAD /* @min:%2eunload */]();
                        _core[_DYN_REMOVE_NOTIFICATION_2 /* @min:%2eremoveNotificationListener */](_sdkStatsListener);
                    }
                    catch (e) {
                        // Swallow any errors to ensure core.unload() is still called
                    }
                    _sdkStatsListener = null;
                }
                // Single flush sends both regular telemetry AND SDK stats metrics
                _self[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */](isAsync);
                _core[_DYN_UNLOAD /* @min:%2eunload */] && _core[_DYN_UNLOAD /* @min:%2eunload */](isAsync, _unloadCallback, cbTimeout);
                return result;
            };
            proxyFunctions(_self, _analyticsPlugin, [
                STR_GET_COOKIE_MGR,
                STR_TRACK_EVENT,
                STR_TRACK_PAGE_VIEW,
                "trackPageViewPerformance",
                STR_TRACK_EXCEPTION,
                "_onerror",
                STR_TRACK_TRACE,
                STR_TRACK_METRIC,
                STR_START_TRACK_PAGE,
                STR_STOP_TRACK_PAGE,
                STR_START_TRACK_EVENT,
                STR_STOP_TRACK_EVENT
            ]);
            proxyFunctions(_self, _getCurrentDependencies, [
                STR_TRACK_DEPENDENCY_DATA,
                "addDependencyListener",
                "addDependencyInitializer"
            ]);
            proxyFunctions(_self, _core, [
                STR_ADD_TELEMETRY_INITIALIZER,
                STR_POLL_INTERNAL_LOGS,
                "stopPollingInternalLogs",
                STR_GET_PLUGIN,
                "addPlugin",
                STR_EVT_NAMESPACE,
                "addUnloadCb",
                "updateCfg",
                "onCfgChange",
                // ITraceHost Proxy
                "getTraceCtx",
                "setTraceCtx",
                "startSpan",
                "getActiveSpan",
                "setActiveSpan",
                "setTraceProvider",
                "getTraceProvider"
            ]);
            proxyFunctions(_self, function () {
                var context = properties[_DYN_CONTEXT /* @min:%2econtext */];
                return context ? context.user : null;
            }, [
                STR_SET_AUTHENTICATED_USER_CONTEXT,
                STR_CLEAR_AUTHENTICATED_USER_CONTEXT
            ]);
            // Handle span end event - create telemetry from span data
            function _onEnd(span) {
                if (_otelApi && span && span.isRecording() && !_otelApi.v.cfg.traceCfg.suppressTracing) {
                    // Flip this span to be the "current" span during processing, so any telemetry created during the span processing
                    // is associated with this span
                    useSpan(_core, span, function () {
                        try {
                            // Create trace telemetry for the span
                            var telemetryItem = createTelemetryItemFromSpan(_core, span);
                            if (telemetryItem) {
                                _self.core.track(telemetryItem);
                            }
                        }
                        catch (error) {
                            // Log any errors during trace processing but don't let them break the span lifecycle
                            _throwInternal(_core[_DYN_LOGGER /* @min:%2elogger */], 2 /* eLoggingSeverity.WARNING */, 64 /* _eInternalMessageId.TelemetryInitializerFailed */, "Error processing span - " + dumpObj(error));
                        }
                    });
                }
            }
            function _onException(span, exception, time) {
                if (_otelApi) {
                    // Flip this span to be the "current" span during processing, so any telemetry created during the span processing
                    useSpan(_core, span, function () {
                        try {
                            _self[STR_TRACK_EXCEPTION /* @min:%2etrackException */]({
                                exception: exception,
                                properties: {
                                    time: time
                                }
                            });
                        }
                        catch (error) {
                            // Log any errors during exception processing but don't let them break the span lifecycle
                            _throwInternal(_core[_DYN_LOGGER /* @min:%2elogger */], 2 /* eLoggingSeverity.WARNING */, 64 /* _eInternalMessageId.TelemetryInitializerFailed */, "Error processing exception - " + dumpObj(error));
                        }
                    });
                }
            }
            // Using a function to support the dynamic adding / removal of plugins, so this will always return the current value
            function _getCurrentDependencies() {
                return dependencies;
            }
            function _initDefaults() {
                _evtNamespace = createUniqueNamespace("AISKU");
                _houseKeepingNamespace = null;
                dependencies = null;
                properties = null;
                _sender = null;
                _snippetVersion = null;
                _throttleMgr = null;
                _iKeySentMessage = false;
                _cdnSentMessage = false;
                _sdkVerSentMessage = false;
                _sdkStatsListener = null;
                _cfgSyncPlugin = new CfgSyncPlugin();
            }
            function _removePageEventHandlers() {
                // Remove any registered event handlers
                if (_houseKeepingNamespace) {
                    removePageUnloadEventListener(null, _houseKeepingNamespace);
                    removePageHideEventListener(null, _houseKeepingNamespace);
                }
            }
            function _addUnloadHook(hooks) {
                _core.addUnloadHook(hooks);
            }
        });
    }
    // Analytics Plugin
    /**
     * Get the current cookie manager for this instance
     */
    AppInsightsSku.prototype.getCookieMgr = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Log a user action or other occurrence.
     * @param event - event to be sent
     * @param customProperties - properties that would be included as part of the event
     */
    AppInsightsSku.prototype.trackEvent = function (event, customProperties) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Logs that a page, or similar container was displayed to the user.
     * @param pageView - page view to be sent
     */
    AppInsightsSku.prototype.trackPageView = function (pageView) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Log a bag of performance information via the customProperties field.
     * @param pageViewPerformance - performance information to be sent
     */
    AppInsightsSku.prototype.trackPageViewPerformance = function (pageViewPerformance) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Log an exception that you have caught.
     * @param exception - exception to be sent
     * @param customProperties - Additional data used to filter pages and metrics in the portal. Defaults to empty.
     */
    AppInsightsSku.prototype.trackException = function (exception, customProperties) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Manually send uncaught exception telemetry. This method is automatically triggered
     * on a window.onerror event.
     * @param exception - The exception to be sent.
     */
    AppInsightsSku.prototype._onerror = function (exception) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Log a diagnostic scenario such entering or leaving a function.
     * @param trace - trace to be sent
     * @param customProperties - Additional custom properties to include in the event.
     */
    AppInsightsSku.prototype.trackTrace = function (trace, customProperties) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Log a numeric value that is not associated with a specific event. Typically used
     * to send regular reports of performance indicators.
     *
     * To send a single measurement, just use the `name` and `average` fields
     * of {@link IMetricTelemetry}.
     *
     * If you take measurements frequently, you can reduce the telemetry bandwidth by
     * aggregating multiple measurements and sending the resulting average and modifying
     * the `sampleCount` field of {@link IMetricTelemetry}.
     * @param metric - input object argument. Only `name` and `average` are mandatory.
     * @param customProperties - Additional custom properties to include in the event.
     */
    AppInsightsSku.prototype.trackMetric = function (metric, customProperties) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Starts the timer for tracking a page load time. Use this instead of `trackPageView` if you want to control when the page view timer starts and stops,
     * but don't want to calculate the duration yourself. This method doesn't send any telemetry. Call `stopTrackPage` to log the end of the page view
     * and send the event.
     * @param name - A string that idenfities this item, unique within this HTML document. Defaults to the document title.
     */
    AppInsightsSku.prototype.startTrackPage = function (name) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Stops the timer that was started by calling `startTrackPage` and sends the pageview load time telemetry with the specified properties and measurements.
     * The duration of the page view will be the time between calling `startTrackPage` and `stopTrackPage`.
     * @param   name  - The string you used as the name in startTrackPage. Defaults to the document title.
     * @param   url   - a relative or absolute URL that identifies the page or other item. Defaults to the window location.
     * @param   properties - additional data used to filter pages and metrics in the portal. Defaults to empty.
     * @param   measurements - metrics associated with this page, displayed in Metrics Explorer on the portal. Defaults to empty.
     */
    AppInsightsSku.prototype.stopTrackPage = function (name, url, properties, measurements) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    AppInsightsSku.prototype.startTrackEvent = function (name) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Log an extended event that you started timing with `startTrackEvent`.
     * @param   name  - The string you used to identify this event in `startTrackEvent`.
     * @param   properties - map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
     * @param   measurements -  map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
     */
    AppInsightsSku.prototype.stopTrackEvent = function (name, properties, measurements) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    AppInsightsSku.prototype.addTelemetryInitializer = function (telemetryInitializer) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    // Properties Plugin
    /**
     * Set the authenticated user id and the account id. Used for identifying a specific signed-in user. Parameters must not contain whitespace or ,;=|
     *
     * The method will only set the `authenticatedUserId` and `accountId` in the current page view. To set them for the whole session, you should set `storeInCookie = true`
     * @param authenticatedUserId - The account ID to set
     * @param accountId - The account ID to set
     * @param storeInCookie - Whether the values should be set for the whole session
     */
    AppInsightsSku.prototype.setAuthenticatedUserContext = function (authenticatedUserId, accountId, storeInCookie) {
        if (storeInCookie === void 0) { storeInCookie = false; }
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Clears the authenticated user id and account id. The associated cookie is cleared, if present.
     */
    AppInsightsSku.prototype.clearAuthenticatedUserContext = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    // Dependencies Plugin
    /**
     * Log a dependency call (e.g. ajax)
     * @param dependencyData - dependency data object
     */
    AppInsightsSku.prototype.trackDependencyData = function (dependency) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    // Misc
    /**
     * Attempt to flush data immediately; If executing asynchronously (the default) and
     * you DO NOT pass a callback function then a [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * will be returned which will resolve once the flush is complete. The actual implementation of the `IPromise`
     * will be a native Promise (if supported) or the default as supplied by [ts-async library](https://github.com/nevware21/ts-async)
     * @param async - send data asynchronously when true
     * @param callBack - if specified, notify caller when send is complete, the channel should return true to indicate to the caller that it will be called.
     * If the caller doesn't return true the caller should assume that it may never be called.
     * @returns - If a callback is provided `true` to indicate that callback will be called after the flush is complete otherwise the caller
     * should assume that any provided callback will never be called, Nothing or if occurring asynchronously a
     * [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html) which will be resolved once the unload is complete,
     * the [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html) will only be returned when no callback is provided
     * and async is true.
     */
    AppInsightsSku.prototype.flush = function (async, callBack) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Manually trigger an immediate send of all telemetry still in the buffer using beacon Sender.
     * Fall back to xhr sender if beacon is not supported.
     * @param async - send data asynchronously when true, default is true
     */
    AppInsightsSku.prototype.onunloadFlush = function (async) {
        if (async === void 0) { async = true; }
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Initialize this instance of ApplicationInsights
     * @param legacyMode - MUST always be false, it is no longer supported from v3.x onwards
     * @returns The initialized {@link IApplicationInsights} instance
     */
    AppInsightsSku.prototype.loadAppInsights = function (legacyMode, logger, notificationManager) {
        if (legacyMode === void 0) { legacyMode = false; }
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Overwrite the lazy loaded fields of global window snippet to contain the
     * actual initialized API methods
     * @param snippet - The global snippet
     */
    AppInsightsSku.prototype.updateSnippetDefinitions = function (snippet) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Call any functions that were queued before the main script was loaded
     */
    AppInsightsSku.prototype.emptyQueue = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    AppInsightsSku.prototype.pollInternalLogs = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    AppInsightsSku.prototype.stopPollingInternalLogs = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    AppInsightsSku.prototype.addHousekeepingBeforeUnload = function (appInsightsInstance) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    AppInsightsSku.prototype.getSender = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Unload and Tear down the SDK and any initialized plugins, after calling this the SDK will be considered
     * to be un-initialized and non-operational, re-initializing the SDK should only be attempted if the previous
     * unload call return `true` stating that all plugins reported that they also unloaded, the recommended
     * approach is to create a new instance and initialize that instance.
     * This is due to possible unexpected side effects caused by plugins not supporting unload / teardown, unable
     * to successfully remove any global references or they may just be completing the unload process asynchronously.
     * If you pass isAsync as true and do not provide
     * If you pass isAsync as `true` (also the default) and DO NOT pass a callback function then an [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * will be returned which will resolve once the unload is complete. The actual implementation of the `IPromise`
     * will be a native Promise (if supported) or the default as supplied by [ts-async library](https://github.com/nevware21/ts-async)
     * @param isAsync - Can the unload be performed asynchronously (default)
     * @param unloadComplete - An optional callback that will be called once the unload has completed
     * @param cbTimeout - An optional timeout to wait for any flush operations to complete before proceeding with the
     * unload. Defaults to 5 seconds.
     * @returns Nothing or if occurring asynchronously a [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * which will be resolved once the unload is complete, the [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * will only be returned when no callback is provided and isAsync is true
     */
    AppInsightsSku.prototype.unload = function (isAsync, unloadComplete, cbTimeout) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    AppInsightsSku.prototype.getPlugin = function (pluginIdentifier) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Add a new plugin to the installation
     * @param plugin - The new plugin to add
     * @param replaceExisting - should any existing plugin be replaced, default is false
     * @param doAsync - Should the add be performed asynchronously
     * @param addCb - [Optional] callback to call after the plugin has been added
     */
    AppInsightsSku.prototype.addPlugin = function (plugin, replaceExisting, doAsync, addCb) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Update the configuration used and broadcast the changes to all loaded plugins
     * @param newConfig - The new configuration is apply
     * @param mergeExisting - Should the new configuration merge with the existing or just replace it. Default is to merge.
     */
    AppInsightsSku.prototype.updateCfg = function (newConfig, mergeExisting) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Returns the unique event namespace that should be used
     */
    AppInsightsSku.prototype.evtNamespace = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Add an unload handler that will be called when the SDK is being unloaded
     * @param handler - the handler
     */
    AppInsightsSku.prototype.addUnloadCb = function (handler) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Add an ajax listener which is called just prior to the request being sent and before the correlation headers are added, to allow you
     * to access the headers and modify the values used to generate the distributed tracing correlation headers. (added in v2.8.4)
     * @param dependencyListener - The Telemetry Initializer function
     * @returns - A IDependencyListenerHandler to enable the initializer to be removed
     */
    AppInsightsSku.prototype.addDependencyListener = function (dependencyListener) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Add an dependency telemetry initializer callback function to allow populating additional properties or drop the request.
     * It is called after the dependency call has completed and any available performance details are available. A dependency
     * initializer is similar to the TelemetryInitializer function but it allows you to block the reporting of the dependency
     * request so that it doesn't count against the `maxAjaxCallsPerView`.
     * @param dependencyInitializer - The Dependency Telemetry Initializer function
     * @returns - A IDependencyInitializerHandler to enable the initializer to be removed
     */
    AppInsightsSku.prototype.addDependencyInitializer = function (dependencyInitializer) {
        return null;
    };
    /**
     * Gets the current distributed trace context for this instance if available
     */
    AppInsightsSku.prototype.getTraceCtx = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Sets the current distributed trace context for this instance if available
     */
    AppInsightsSku.prototype.setTraceCtx = function (newTraceCtx) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Watches and tracks changes for accesses to the current config, and if the accessed config changes the
     * handler will be recalled.
     * @param handler - The handler to call when the config changes
     * @returns A watcher handler instance that can be used to remove itself when being unloaded
     */
    AppInsightsSku.prototype.onCfgChange = function (handler) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Start a new span with the given name and optional parent context.
     *
     * Note: This method only creates and returns the span. It does not automatically
     * set the span as the active trace context. Context management should be handled
     * separately using setTraceCtx() if needed.
     *
     * @param name - The name of the span
     * @param options - Options for creating the span (kind, attributes, startTime)
     * @param parent - Optional parent context. If not provided, uses the current active trace context
     * @returns A new span instance, or null if no trace provider is available
     * @since 3.4.0
     */
    AppInsightsSku.prototype.startSpan = function (name, options, parent) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Return the current active span, if no trace provider is available null will be returned
     * but when a trace provider is available a span instance will always be returned, even if
     * there is no active span (in which case a non-recording span will be returned).
     * @param createNew - Optional flag to create a non-recording span if no active span exists, defaults to true.
     * When false, returns the existing active span or null without creating a non-recording span.
     * @returns The current active span or null if no trace provider is available or if createNew is false and no active span exists
     * @since 3.4.0
     */
    AppInsightsSku.prototype.getActiveSpan = function (createNew) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Set the current Active Span, if no trace provider is available the span will be not be set as the active span.
     * @param span - The span to set as the active span
     * @returns An ISpanScope instance that provides the current scope, the span will always be the span passed in
     * even when no trace provider is available
     * @since 3.4.0
     */
    AppInsightsSku.prototype.setActiveSpan = function (span) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Set the trace provider for creating spans.
     * This allows different SKUs to provide their own span implementations.
     *
     * @param provider - The trace provider to use for span creation
     * @since 3.4.0
     */
    AppInsightsSku.prototype.setTraceProvider = function (provider) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Get the current trace provider.
     *
     * @returns The current trace provider, or null if none is set
     * @since 3.4.0
     */
    AppInsightsSku.prototype.getTraceProvider = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    return AppInsightsSku;
}());
export { AppInsightsSku };
// tslint:disable-next-line
export function _findSdkSourceFile() {
    if (_internalSdkSrc) {
        // Use the cached value
        return _internalSdkSrc;
    }
    var sdkSrc = null;
    var isModule = false;
    var cdns = [
        "://js.monitor.azure.com/",
        "://az416426.vo.msecnd.net/"
    ];
    try {
        // Try and determine whether the sdk is being loaded from the CDN
        // currentScript is only valid during initial processing
        var scrpt = (document || {}).currentScript;
        if (scrpt) {
            sdkSrc = scrpt.src;
            // } else {
            //     // We need to update to at least typescript 2.9 for this to work :-(
            //     // Leaving as a stub for now so after we upgrade this breadcrumb is available
            //     let meta = import.meta;
            //     sdkSrc = (meta || {}).url;
            //     isModule = true;
        }
    }
    catch (e) {
        // eslint-disable-next-line no-empty
    }
    if (sdkSrc) {
        try {
            var url_2 = sdkSrc.toLowerCase();
            if (url_2) {
                var src_1 = "";
                arrForEach(cdns, function (value, idx) {
                    if (strIndexOf(url_2, value) !== -1) {
                        src_1 = "cdn" + (idx + 1);
                        if (strIndexOf(url_2, "/scripts/") === -1) {
                            if (strIndexOf(url_2, "/next/") !== -1) {
                                src_1 += "-next";
                            }
                            else if (strIndexOf(url_2, "/beta/") !== -1) {
                                src_1 += "-beta";
                            }
                        }
                        _internalSdkSrc = src_1 + (isModule ? ".mod" : "");
                        return -1;
                    }
                });
            }
        }
        catch (e) {
            // eslint-disable-next-line no-empty
        }
        // Cache the found value so we don't have to look it up again
        _internalSdkSrc = sdkSrc;
    }
    return _internalSdkSrc;
}
//# sourceMappingURL=AISku.js.map